import React, { useState, useLayoutEffect, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Heading } from '@web/atoms';
import { CtaButton } from '@web/molecules/index.js';
import { CTAGroup } from '../CTAGroup/index.js';
import {
  BackgroundColorOverlay,
  ButtonWrapper,
  ContentWrapper,
  FullWidthDivider,
  GradientOverlay,
  ImagePanel,
  InkIndicator,
  PanelBody,
  PanelContent,
  PanelHeadline,
  PanelImage,
  PanelOverlay,
  StyledTabbedShowcase,
  StyledTabPanelsWrapper,
  SubHeadline,
  Tab,
  TabBar,
} from './styles.js';

// Minimum horizontal drag distance to count as a swipe rather than a tap.
const SWIPE_THRESHOLD_PX = 50;

export const TabbedShowcase = ({
  headline,
  headlineTypography = 'heading1',
  fontColor = 'text.primary',
  subheadline,
  backgroundColor = 'background.base',
  tabPanels,
  cta,
}) => {
  const [activeTab, setActiveTab] = useState(tabPanels[0]?.tabLabel);
  const [visibleTab, setVisibleTab] = useState(tabPanels[0]?.tabLabel);
  const [imgFading, setImgFading] = useState(false);
  const [loadedImages, setLoadedImages] = useState({});
  const btnRefs = useRef({});
  const railRef = useRef(null);
  const inkRef = useRef(null);
  const timerRef = useRef(null);
  // Coordinates of the active touch's start point, or null when no swipe is in progress.
  const touchStartRef = useRef(null);

  // activeTab drives ink position and tab styling immediately.
  // visibleTab drives the image panel and is delayed to create a cross-fade:
  // the outgoing image fades out while the text still shows the old panel's
  // content, then both flip once the fade completes.
  const switchTab = tabLabel => {
    if (tabLabel === activeTab) return;
    // Scroll the tab into view horizontally when the bar overflows on mobile.
    btnRefs.current[tabLabel]?.scrollIntoView({
      inline: 'nearest',
      block: 'nearest',
    });
    setActiveTab(tabLabel);
    setImgFading(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setVisibleTab(tabLabel);
      setImgFading(false);
    }, 220);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Swipes to the next/previous tab on mobile. Direction is only decided on
  // touch end (not touchmove/preventDefault) so native vertical page scroll
  // is never interfered with.
  const handleTouchStart = e => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = e => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start || tabPanels?.length <= 1) return;

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    // Ignore drags that are too short, or more vertical than horizontal
    // since those are page scrolls, not tab swipes
    if (
      Math.abs(deltaX) < SWIPE_THRESHOLD_PX ||
      Math.abs(deltaX) < Math.abs(deltaY)
    ) {
      return;
    }

    // Swiping left moves to the next tab, right moves to the previous one.
    // Swiping past either end is a no-op rather than wrapping around.
    const currentIndex = tabPanels.findIndex(
      ({ tabLabel }) => tabLabel === activeTab,
    );
    const nextPanel = tabPanels[currentIndex + (deltaX < 0 ? 1 : -1)];
    if (nextPanel) switchTab(nextPanel.tabLabel);
  };

  // Marks a tab's image as loaded so the overlay can wait for the real paint
  // instead of assuming it's ready as soon as the panel becomes visible.
  const handleImageLoad = tabLabel => {
    setLoadedImages(prev =>
      prev[tabLabel] ? prev : { ...prev, [tabLabel]: true },
    );
  };

  // Derived from visibleTab so the panel text holds until the cross-fade ends.
  const currentPanel = tabPanels?.find(
    ({ tabLabel }) => tabLabel === visibleTab,
  );
  // Panels without an image (background-color only) have nothing to wait for.
  const imageReady = !currentPanel?.image || !!loadedImages[visibleTab];

  // Measures the active tab button's position relative to the rail and writes
  // directly to the ink indicator's DOM style, avoiding a React re-render.
  const updateInk = () => {
    const btn = btnRefs.current[activeTab];
    const rail = railRef.current;
    const ink = inkRef.current;
    if (!btn || !rail || !ink) return;
    const railRect = rail.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    // getBoundingClientRect() is viewport-relative, but position:absolute inside
    // a scrollable container is content-relative, so scrollLeft must be added.
    ink.style.left = `${btnRect.left - railRect.left + rail.scrollLeft}px`;
    ink.style.width = `${btnRect.width}px`;
  };

  // Ref so async callbacks (ResizeObserver, fonts.ready, resize) always call
  // the latest closure without needing to re-register when activeTab changes.
  const updateInkRef = useRef(updateInk);
  updateInkRef.current = updateInk;

  useEffect(() => {
    const handler = () => updateInkRef.current();
    // ResizeObserver re-measures when a tab's font-weight toggle changes its
    // width, firing after layout is computed with the new styles applied.
    const observer = new ResizeObserver(handler);
    Object.values(btnRefs.current).forEach(btn => {
      if (btn) observer.observe(btn);
    });
    // Correct the initial measurement once the web font is ready, since
    // getBoundingClientRect on mount may use fallback font metrics.
    document.fonts.ready.then(handler);
    window.addEventListener('resize', handler);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handler);
    };
  }, []);

  // Positions the ink synchronously before paint on each tab change.
  useLayoutEffect(updateInk, [activeTab]);

  return (
    <StyledTabbedShowcase $backgroundColor={backgroundColor}>
      <ContentWrapper>
        <Heading
          headingLevel="h1"
          typography={headlineTypography}
          headingText={headline}
          horizontalPadding={2}
          verticalPadding={1}
          backgroundColor={backgroundColor}
          fontColor={fontColor}
          position="center"
        />
        {subheadline && <SubHeadline>{subheadline}</SubHeadline>}
        <StyledTabPanelsWrapper $oneTab={tabPanels?.length === 1}>
          {tabPanels?.length > 1 && (
            <>
              <TabBar
                ref={railRef}
                role="tablist"
                aria-label="Ollie product tabs"
                $alignTabs={tabPanels?.length > 3 ? 'flex-start' : 'center'}
              >
                {tabPanels?.map(
                  ({ tabLabel, activeLabelColor, inactiveLabelColor }) => (
                    <Tab
                      key={tabLabel}
                      ref={el => {
                        btnRefs.current[tabLabel] = el;
                      }}
                      id={`bb-tab-${tabLabel}`}
                      role="tab"
                      aria-selected={activeTab === tabLabel}
                      aria-controls="bb-panel"
                      $isActive={activeTab === tabLabel}
                      $activeLabelColor={activeLabelColor}
                      $inactiveLabelColor={inactiveLabelColor}
                      onClick={() => switchTab(tabLabel)}
                    >
                      {tabLabel}
                    </Tab>
                  ),
                )}
                <InkIndicator aria-hidden="true" ref={inkRef} />
              </TabBar>
              <FullWidthDivider />
            </>
          )}
          <ImagePanel
            role="tabpanel"
            id="bb-panel"
            aria-labelledby={
              tabPanels?.length > 1 ? `bb-tab-${activeTab}` : undefined
            }
            $oneTab={tabPanels?.length === 1}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {tabPanels?.map(
              ({ tabLabel, image, headline, mobileImageHorizontalPosition }) =>
                image ? (
                  <PanelImage
                    key={tabLabel}
                    src={image?.src ?? image}
                    alt={image?.alt ?? headline}
                    fill
                    objectFit="cover"
                    sizes="(max-width: 640px) 100vw, 1200px"
                    $visible={tabLabel === visibleTab && !imgFading}
                    $mobileImageHorizontalPosition={
                      mobileImageHorizontalPosition
                    }
                    onLoad={() => handleImageLoad(tabLabel)}
                  />
                ) : null,
            )}
            {currentPanel?.backgroundColor && !currentPanel?.image ? (
              <BackgroundColorOverlay
                data-testid="background-color-overlay"
                $backgroundColor={currentPanel.backgroundColor}
              />
            ) : (
              <GradientOverlay aria-hidden="true" />
            )}
            {currentPanel?.overlay?.[0] && (
              <PanelOverlay $visible={!imgFading && imageReady}>
                {imageReady ? currentPanel.overlay[0] : null}
              </PanelOverlay>
            )}
            {currentPanel && (
              <PanelContent $fading={imgFading}>
                <PanelHeadline
                  $typography={currentPanel.typography}
                  $fontColor={currentPanel.fontColor}
                >
                  {currentPanel.headline}
                </PanelHeadline>
                {currentPanel.subheadline && (
                  <PanelBody>{currentPanel.subheadline}</PanelBody>
                )}
              </PanelContent>
            )}
          </ImagePanel>
        </StyledTabPanelsWrapper>
      </ContentWrapper>
      {cta && (
        <ButtonWrapper>
          {cta.component === 'cta_group' ? (
            <CTAGroup {...cta} />
          ) : (
            <CtaButton
              {...cta}
              href={cta.target}
              variant={cta.variant}
              icon={cta.icon}
              iconAlignment={cta.iconAlignment}
              headerText={cta.headerText}
            >
              {cta.text}
            </CtaButton>
          )}
        </ButtonWrapper>
      )}
    </StyledTabbedShowcase>
  );
};

TabbedShowcase.displayName = 'TabbedShowcase';

TabbedShowcase.propTypes = {
  backgroundColor: PropTypes.string,
  fontColor: PropTypes.string,
  headline: PropTypes.string,
  headlineTypography: PropTypes.string,
  subheadline: PropTypes.node,
  tabPanels: PropTypes.arrayOf(
    PropTypes.shape({
      tabLabel: PropTypes.string,
      headline: PropTypes.string,
      typography: PropTypes.string,
      fontColor: PropTypes.string,
      activeLabelColor: PropTypes.string,
      inactiveLabelColor: PropTypes.string,
      subheadline: PropTypes.node,
      image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      mobileImageHorizontalPosition: PropTypes.number,
      backgroundColor: PropTypes.string,
      overlay: PropTypes.arrayOf(PropTypes.node),
    }),
  ).isRequired,
  cta: PropTypes.object,
};
